/**
 * DB row → domain type mappers.
 * Centralize all conversions so UI code never constructs domain objects from raw rows.
 */

import type { Card, Deck, Folder, StudyModes, TestData } from "@/types/linguadeck";
import type { CardsRow, DecksRow, DeckSummaryRow, FoldersRow } from "@/types/database";

const DEFAULT_STUDY_MODES: StudyModes = { cards: true, learn: true, test: true, match: true, philology: true };
const VALID_DECK_LANGS = ["en", "es", "fr", "de", "it", "nl", "pl", "pt-BR", "tr", "ko", "ru", "uk"] as const;

function toDeckLang(s: string | null | undefined): (typeof VALID_DECK_LANGS)[number] {
  if (typeof s !== "string" || !s.trim()) return "en";
  const normalized = s.trim();
  return VALID_DECK_LANGS.includes(normalized as (typeof VALID_DECK_LANGS)[number])
    ? (normalized as (typeof VALID_DECK_LANGS)[number])
    : "en";
}

function toCardType(s: string | null | undefined): "grammar" | "vocabulary" | undefined {
  if (s === "grammar" || s === "vocabulary") return s;
  return undefined;
}

function parseStudyModes(raw: StudyModes | string | Record<string, unknown> | null | undefined): StudyModes {
  if (raw == null) return { ...DEFAULT_STUDY_MODES };
  const parsed =
    typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw) as Record<string, unknown>;
          } catch {
            return null;
          }
        })()
      : raw;
  if (parsed && typeof parsed === "object") {
    return { ...DEFAULT_STUDY_MODES, ...parsed } as StudyModes;
  }
  return { ...DEFAULT_STUDY_MODES };
}

/** Deck list item: Deck with cards=[] and cardCount (for library lists). */
export interface DeckListItem extends Deck {
  cards: Card[];
  cardCount?: number;
}

/** Map decks table row + cardCount → DeckListItem. */
export function mapDeckRowToListItem(deck: DecksRow, cardCount: number): DeckListItem {
  return {
    id: deck.id,
    title: deck.title,
    lang: toDeckLang(deck.lang),
    emoji: deck.emoji ?? "📚",
    level: deck.level ?? undefined,
    type: (deck.type as "words" | "cards" | "tests") ?? "cards",
    deckType: deck.deck_type ?? undefined,
    shareToken: deck.share_token ?? null,
    shareAccess: deck.share_access ?? "read",
    studyModes: parseStudyModes(deck.study_modes),
    created_at: deck.created_at ?? undefined,
    cards: [],
    cardCount,
  };
}

/** Normalized total card count for a deck. Use for display in folder/library. */
export function getDeckCardCount(deck: { cardCount?: number; cards?: unknown[] }): number {
  if (deck.cardCount != null && deck.cardCount >= 0) return deck.cardCount;
  const len = Array.isArray(deck.cards) ? deck.cards.length : 0;
  return len;
}

/** Map deck_summaries view row → DeckListItem (single source of truth for library). */
export function mapDeckSummaryRowToDeckListItem(row: DeckSummaryRow): DeckListItem {
  return {
    id: row.deck_id,
    title: row.title,
    description: row.description ?? undefined,
    lang: toDeckLang(row.source_language),
    emoji: row.emoji ?? "📚",
    level: row.level ?? undefined,
    type: (row.type as "words" | "cards" | "tests") ?? "cards",
    deckType: row.deck_type ?? undefined,
    shareToken: row.share_token ?? null,
    shareAccess: row.share_access ?? "read",
    studyModes: parseStudyModes(row.study_modes),
    created_at: row.created_at ?? undefined,
    cards: [],
    cardCount: row.cards_count,
    learnableCount: row.learnable_count,
    testableCount: row.testable_count,
    gamesCount: row.games_count,
  };
}

/** Map cards table row → Card domain type. */
export function mapCardRow(row: CardsRow): Card {
  return {
    id: row.id,
    front: row.front,
    back: row.back,
    frontLang: row.front_language ?? undefined,
    backLang: row.back_language ?? undefined,
    hint: row.hint ?? undefined,
    imageUrl: row.image_url ?? undefined,
    frontAudioUrl: row.front_audio_url ?? undefined,
    backAudioUrl: row.back_audio_url ?? undefined,
    frontAudioLocale: row.front_audio_locale ?? undefined,
    backAudioLocale: row.back_audio_locale ?? undefined,
    cardType: toCardType(row.card_type),
    level: row.level ?? undefined,
    aiExplanation: row.ai_explanation ?? undefined,
    definition: row.definition ?? undefined,
    example: row.example ?? undefined,
    testData: (row.test_data as TestData) ?? undefined,
    repetition_count: row.repetition_count ?? 0,
    easiness_factor: row.easiness_factor ?? 2.5,
    interval_days: row.interval_days ?? 0,
    next_review: row.next_review ?? null,
  };
}

/** Map decks + cards rows → full Deck[] with cards. */
export function mapDeckRowsWithCards(deckRows: DecksRow[], cardRows: CardsRow[]): Deck[] {
  const cardsByDeckId = new Map<string, Card[]>();
  for (const cardRow of cardRows) {
    const mapped = mapCardRow(cardRow);
    const existing = cardsByDeckId.get(cardRow.deck_id);
    if (existing) existing.push(mapped);
    else cardsByDeckId.set(cardRow.deck_id, [mapped]);
  }
  return deckRows.map((deck) => {
    const cards = cardsByDeckId.get(deck.id) ?? [];
    return {
      id: deck.id,
      title: deck.title,
      lang: toDeckLang(deck.lang),
      emoji: deck.emoji ?? "📚",
      level: deck.level ?? undefined,
      type: (deck.type as "words" | "cards" | "tests") ?? "cards",
      deckType: deck.deck_type ?? undefined,
      shareToken: deck.share_token ?? null,
      shareAccess: deck.share_access ?? "read",
      studyModes: parseStudyModes(deck.study_modes),
      created_at: deck.created_at ?? undefined,
      cards,
      cardCount: cards.length,
    };
  });
}

/** Map folders row (or folder relation) → Folder domain type. */
export function mapFolderRow(
  data: { id: string; user_id: string; title: string; created_at: string; tags?: unknown; share_token?: string | null },
  shareToken?: string | null
): Folder {
  const tagsRaw = data.tags;
  const tags: string[] = Array.isArray(tagsRaw)
    ? tagsRaw.filter((t): t is string => typeof t === "string")
    : [];
  return {
    id: data.id,
    user_id: data.user_id,
    title: data.title,
    created_at: data.created_at,
    tags,
    shareToken:
      typeof shareToken === "string"
        ? shareToken
        : typeof data.share_token === "string"
          ? data.share_token
          : null,
  };
}

export { toDeckLang, parseStudyModes };
